import React, { useMemo, useState } from "react";
import { Chart as ChartJS, LogarithmicScale } from "chart.js";
import LineChart from "../../shared/components/Charts/LineChart";
import kineticsDemoData from "../data/kineticsDemoData.json";
import "./styling/KineticsDemoChart.css";

ChartJS.register(LogarithmicScale);

/**
 * KineticsDemoChart — homepage marketing preview of the Kinetics & Recovery
 * capability. Plots a small, fixed set of illustrative recovery curves (not
 * real customer data) so the shape of the app's actual kinetics chart is
 * visible without calling the API or selecting ore properties/metals.
 */

const CURVE_COLORS = [
  "#8b7fd6",
  "#6ea8fe",
  "#e08fd6",
  "#7fd6c1",
  "#d6b57f",
  "#7f9fd6",
];

const KineticsDemoChart = () => {
  const [plotMode, setPlotMode] = useState("cumulative"); // 'cumulative' | 'daily'
  const [xScale, setXScale] = useState("linear"); // 'linear' | 'log'

  const chartData = useMemo(() => {
    const datasets = kineticsDemoData.series.map((s, i) => ({
      label: s.label,
      data: kineticsDemoData.days.map((day, idx) => ({
        x: day,
        y: (plotMode === "cumulative" ? s.cumulative[idx] : s.daily[idx]) * 100,
      })),
      borderColor: CURVE_COLORS[i % CURVE_COLORS.length],
      backgroundColor: CURVE_COLORS[i % CURVE_COLORS.length] + "20",
      tension: 0.1,
      pointRadius: 0,
      borderWidth: 2,
    }));
    return { datasets };
  }, [plotMode]);

  const yLabel = plotMode === "cumulative" ? "Cumulative Recovery (%)" : "Daily Extraction Rate (%)";

  const customOptions = {
    scales: {
      x: {
        type: xScale === "log" ? "logarithmic" : "linear",
        min: kineticsDemoData.days[0],
        max: kineticsDemoData.days[kineticsDemoData.days.length - 1],
        title: { display: true, text: "Days", color: "#e2e8f0", font: { family: "'Inter', sans-serif", size: 12, weight: "500" } },
        ticks: { color: "#e2e8f0", font: { family: "'Inter', sans-serif", size: 10 } },
        grid: { color: "#374151" },
      },
      y: {
        title: { display: true, text: yLabel, color: "#e2e8f0", font: { family: "'Inter', sans-serif", size: 12, weight: "500" } },
        ticks: { color: "#e2e8f0", font: { family: "'Inter', sans-serif", size: 10 } },
        min: 0,
        grid: { color: "#374151" },
      },
    },
    plugins: {
      legend: { display: false },
    },
  };

  return (
    <div className="home-kinetics-chart">
      <div className="home-kinetics-chart__controls">
        <div className="home-kinetics-chart__toggle-group" role="group" aria-label="Plot mode">
          {[{ id: "cumulative", label: "Cumulative" }, { id: "daily", label: "Daily" }].map((m) => (
            <button
              key={m.id}
              type="button"
              className={`home-kinetics-chart__toggle${plotMode === m.id ? " active" : ""}`}
              onClick={() => setPlotMode(m.id)}
            >
              {m.label}
            </button>
          ))}
        </div>
        <div className="home-kinetics-chart__toggle-group" role="group" aria-label="X-axis scale">
          {[{ id: "linear", label: "Linear" }, { id: "log", label: "Log" }].map((m) => (
            <button
              key={m.id}
              type="button"
              className={`home-kinetics-chart__toggle${xScale === m.id ? " active" : ""}`}
              onClick={() => setXScale(m.id)}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>
      <div className="home-kinetics-chart__chart-wrap">
        <LineChart data={chartData} height="100%" showLegend={false} customOptions={customOptions} />
      </div>
    </div>
  );
};

export default KineticsDemoChart;
